import React, { useRef } from 'react'
import styled from 'styled-components'
import { space, SpaceProps, layout, LayoutProps } from 'styled-system'

import { Flex, Text } from 'components'
import { colors } from 'utils/colors'

type CardProps = SpaceProps &
  LayoutProps & {
    actionItems?: React.ReactNode
    body?: string | React.ReactNode
    title?: string | React.ReactNode
    isActive?: boolean
    isClickable?: boolean
    onClick?: () => void
    display?: string
    tag?: React.ReactNode
    testId?: string
  }

const Card: React.FC<CardProps> = ({
  actionItems,
  body,
  children,
  isActive = false,
  isClickable = false,
  onClick,
  title,
  tag,
  display,
  testId,
  ...cssProps
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null)

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPos.current = { x: e.clientX, y: e.clientY }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!mouseDownPos.current) return
    const dx = Math.abs(e.clientX - mouseDownPos.current.x)
    const dy = Math.abs(e.clientY - mouseDownPos.current.y)
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < 5) {
      onClick?.()
    } else {
      e.stopPropagation()
    }

    mouseDownPos.current = null
  }

  return (
    <Container
      data-testid={testId}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      isActive={isActive}
      isClickable={isClickable}
      ref={parentRef}
      {...cssProps}
    >
      {(title || actionItems) && (
        <CardHead>
          <Text lineHeight="24px" color={isActive ? 'gray7' : 'gray5'} fontWeight="bold" fontSize={16}>
            {title}
          </Text>

          <Flex lineHeight="24px" alignItems="center" justifyContent="space-between" gap="8px">
            <ActionItems>{actionItems}</ActionItems>
          </Flex>
        </CardHead>
      )}
      <CardBody display={display}>{body || children}</CardBody>
    </Container>
  )
}

export default Card

const Container = styled.div<SpaceProps & { isActive: boolean; isClickable: boolean }>`
  ${space}
  ${layout}
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #e4e9f2;
  cursor: ${(p) => (p.isClickable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background-color: ${colors.gray1};
  }
`

const CardHead = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`

const ActionItems = styled.div`
  display: flex;

  & > div {
    cursor: pointer;
    position: relative;

    & > svg {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`

const CardBody = styled.div<{ display?: string }>`
  display: ${(p) => p.display || 'flex'};
  width: 100%;
  justify-content: space-between;
  gap: 0.2rem;
  align-items: center;
`
